import { PaginationRequest, PaginationResult } from '@shared/pagination';

import { DrugUtilizationPeriod } from '@/features/drug-utilization';
import { artiaProtectedApi } from '@/lib/api-client';
import { Benefit } from '@/types/benefit';
import { CoverageTagLookup, CoverageTagPackaging, coverageTagToLookup } from '@/types/coverage-tag-lookup';
import { Lookup } from '@/types/lookup';
import { NestedLookup } from '@/types/nested-lookup';
import { Pool } from '@/types/pool';
import { State } from '@/types/state';
import { buildQueryString } from '@/utils/filtering-functions';

export const useLookupsService = () => {
  /**
   * Returns a collection of lookups.
   *
   * @param query The query (client name) to match against.
   * @param assignedOnly True if the results should be limited to clients the
   * user is assigned to, false otherwise.
   */
  const getClientLookups = ({ query: q, assignedOnly = false, ...rest }: ClientsLookupQuery): Promise<PaginationResult<Lookup> | Lookup[]> => {
    return artiaProtectedApi.get(`lookups/clients${buildQueryString({ q, 'assigned-only': assignedOnly, ...rest })}`);
  };

  const getMarketBasketLookups = (query?: string, assignedOnly = false, bidAnalysisBasketsOnly = true): Promise<NestedLookup[]> => {
    let url = `lookups/market-baskets?assigned-only=${assignedOnly}&bid-analysis-baskets-only=${bidAnalysisBasketsOnly}`;

    if (query) {
      url = `${url}&q=${query}`;
    }

    return artiaProtectedApi.get(url);
  };

  const getPaTypeLookups = (): Promise<Lookup[]> => {
    return artiaProtectedApi.get('lookups/pa-types');
  };

  const getProductLookups = ({ query: q, ...rest }: ProductsLookupQuery = {}): Promise<PaginationResult<Lookup>> => {
    return artiaProtectedApi.get(`lookups/products${buildQueryString({ q, ...rest })}`);
  };

  const getStates = (): Promise<State[]> => {
    return artiaProtectedApi.get('states');
  };

  const getClassifications = ({ query: q, ... rest }: ClassificationsLookupQuery = {}): Promise<Lookup[]> => {
    const url = `lookups/classifications${buildQueryString({ q, ...rest })}`;
    return artiaProtectedApi.get(url);
  };

  const getClassificationsPaged = ({ query: q, ... rest }: ClassificationsPagedLookupQuery = {}): Promise<PaginationResult<Lookup>> => {
    const url = `lookups/classifications/paged${buildQueryString({ q, ...rest })}`;
    return artiaProtectedApi.get(url);
  };

  const getPools = (): Promise<Pool[]> => {
    return artiaProtectedApi.get('states/pools');
  };

  const getBenefits = (): Promise<Benefit[]> => {
    return artiaProtectedApi.get('benefits');
  };

  const getDrugUtilizationYears = (): Promise<Lookup[]> => {
    return artiaProtectedApi.get('lookups/drug-utilization-years');
  };

  const getDrugUtilizationPeriods = (): Promise<DrugUtilizationPeriod[]> => {
    return artiaProtectedApi.get('lookups/drug-utilization-periods');
  };

  const getAnalystLookups = async ({ query: q, ...rest }: AnalystsLookupQuery = {}): Promise<PaginationResult<Lookup> | Lookup[]> => {
    return artiaProtectedApi.get(`lookups/analysts${buildQueryString({ q, ...rest })}`);
  };

  const getClinicalsChangeTypes = (): Promise<Lookup[]> => {
    return artiaProtectedApi.get('lookups/clinicals-change-types');
  };

  const getBoardMeetingTypes = (): Promise<Lookup[]> => {
    return artiaProtectedApi.get('lookups/board-meeting-types');
  };

  const getSupportedTimezones = (): Promise<Lookup<string>[]> => {
    return artiaProtectedApi.get('lookups/supported-timezones');
  };

  const getCoverageTags = async ({ query: q, ...rest }: CoverageTagsLookupQuery): Promise<PaginationResult<Lookup>> => {
    const result = await artiaProtectedApi.get(
      `lookups/coverage-tags${buildQueryString({ q, ...rest })}`
    ) as PaginationResult<CoverageTagLookup>;

    return {
      ...result,
      items: result.items.map(coverageTagToLookup)
    };
  };

  const getAllCoverageTags = async (): Promise<Lookup[]> => {
    return artiaProtectedApi.get('lookups/all-coverage-tags');
  };

  const getRawCoverageTags = ({ query: q, ...rest }: CoverageTagsLookupQuery): Promise<PaginationResult<CoverageTagLookup> & { untaggedPackagingCount: number; untaggedPackagings: CoverageTagPackaging[] }> => {
    return artiaProtectedApi.get(
      `lookups/coverage-tags${buildQueryString({ q, ...rest })}`
    );
  };

  return {
    getAnalystLookups,
    getClientLookups,
    getDrugUtilizationPeriods,
    getDrugUtilizationYears,
    getMarketBasketLookups,
    getPaTypeLookups,
    getProductLookups,
    getStates,
    getPools,
    getClassifications,
    getClassificationsPaged,
    getBenefits,
    getClinicalsChangeTypes,
    getBoardMeetingTypes,
    getSupportedTimezones,
    getCoverageTags,
    getAllCoverageTags,
    getRawCoverageTags
  };
};

type CoverageTagsLookupQuery = {
  query?: string;
  defaultOnly?: boolean;
  drugId?: number;
  removeIfCarvedOutInState?: string;
  removeIfPdlExemptInState?: string;
} & PaginationRequest;

type ProductsLookupQuery = {
  query?: string;
  classifications?: number[];
  trackedByClients?: number[];
  trackedOnly?: boolean;
  clientId?: number;
  excludeMarketBasketId?: number;
} & PaginationRequest;

type AnalystsLookupQuery = {
  query?: string;
} & PaginationRequest;

type ClientsLookupQuery = {
  query?: string;
  assignedOnly?: boolean;
} & PaginationRequest;

type ClassificationsLookupQuery = {
  query?: string;
  trackedByClients?: number[];
  removeIfCarvedOutInState?: string;
  removeIfPdlExemptInState?: string;
  trackedOnly?: boolean;
};

type ClassificationsPagedLookupQuery = {
  query?: string;
  trackedByClients?: number[];
  removeIfCarvedOutInState?: string;
  removeIfPdlExemptInState?: string;
  trackedOnly?: boolean;
} & PaginationRequest;
